import { api } from './client';
import type { ActivityInterval, ActivityScope } from './user-activity';

export interface ActivityDetailParams {
  metricType: 'features' | 'tasks' | 'decisions' | 'sessions' | 'epicRequests' | 'epics' | 'implementationTime';
  interval: ActivityInterval;
  page: number;
  scope: ActivityScope;
  scopeId?: string;
  timeZone: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  cursor?: string;
}

export interface FeatureDetail {
  id: string;
  identifier: string;
  title: string;
  epicName: string;
  statusName: string;
  statusColor?: string;
  assigneeName?: string;
  createdAt: string;
}

export interface TaskDetail {
  id: string;
  identifier: string;
  title: string;
  featureIdentifier: string;
  featureTitle: string;
  statusName: string;
  assigneeName?: string;
  createdAt: string;
  completedAt: string | null;
}

export interface DecisionDetail {
  id: string;
  question: string;
  decision: string;
  rationale?: string;
  category: string;
  impact: string;
  madeBy: string;
  epicName: string;
  epicId: string;
  featureIdentifier?: string;
  createdAt: string;
}

export type SessionType =
  | 'explicit'
  | 'er_creation'
  | 'er_conversion'
  | 'planning'
  | 'implementation';

export interface SessionDetail {
  id: string;
  sessionType?: SessionType;
  epicId?: string;
  epicName?: string;
  startedAt: string;
  endedAt?: string;
  status: string;
  summary?: string;
  entityTitle?: string;
  entityId?: string;
}

export interface EpicRequestDetail {
  id: string;
  title: string;
  identifier: string;
  status: string;
  requestedByName?: string;
  teamName?: string | null;
  createdAt: string;
}

export interface EpicDetail {
  id: string;
  name: string;
  identifier: string;
  teamName?: string | null;
  featureCount: number;
  creatorName?: string;
  createdAt: string;
}

export interface ImplementationTimeDetail {
  id: string;
  identifier: string;
  title: string;
  durationMinutes: number;
  epicName: string;
  statusName: string;
  statusColor?: string;
  completedAt: string;
}

export type ActivityDetailItem =
  | FeatureDetail
  | TaskDetail
  | DecisionDetail
  | SessionDetail
  | EpicRequestDetail
  | EpicDetail
  | ImplementationTimeDetail;

export interface ActivityDetailsResponse {
  data: ActivityDetailItem[];
  meta: {
    cursor?: string;
    hasMore: boolean;
  };
}

export function getActivityDetails(
  params: ActivityDetailParams
): Promise<ActivityDetailsResponse> {
  const searchParams = new URLSearchParams();
  searchParams.append('metricType', params.metricType);
  searchParams.append('interval', params.interval);
  searchParams.append('page', String(params.page));
  searchParams.append('timeZone', params.timeZone);
  searchParams.append('scope', params.scope);
  if (params.scopeId) searchParams.append('scopeId', params.scopeId);
  if (params.startDate !== undefined) searchParams.append('startDate', params.startDate);
  if (params.endDate !== undefined) searchParams.append('endDate', params.endDate);
  if (params.limit) searchParams.append('limit', String(params.limit));
  if (params.cursor) searchParams.append('cursor', params.cursor);

  const qs = searchParams.toString();
  return api.get<ActivityDetailsResponse>(
    `/user-activity/details${qs ? `?${qs}` : ''}`
  );
}
